import { createSlice } from '@reduxjs/toolkit';
import type { UserPartyAssignment } from '../../services/api/userPartyAssignmentService';
import {
  loadUserPartyAssignments,
  createUserPartyAssignment,
  updateUserPartyAssignment,
  deleteUserPartyAssignment,
  updateUserAffiliation,
} from '../thunks/userPartyAssignmentThunks';

export interface UserPartyAssignmentState {
  items: UserPartyAssignment[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  status: 'idle' | 'loading' | 'success' | 'failure';
  lastError: string | null;
}

const initialState: UserPartyAssignmentState = {
  items: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: 50,
  status: 'idle',
  lastError: null,
};

const userPartyAssignmentSlice = createSlice({
  name: 'userPartyAssignment',
  initialState,
  reducers: {
    clearError(state) {
      state.lastError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUserPartyAssignments.pending, (state) => {
        state.status = 'loading';
        state.lastError = null;
      })
      .addCase(loadUserPartyAssignments.fulfilled, (state, action) => {
        state.status = 'success';
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.pageIndex = action.payload.pageIndex;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(loadUserPartyAssignments.rejected, (state, action) => {
        state.status = 'failure';
        state.lastError = (action.payload as string) ?? action.error.message ?? 'Failed to load assignments';
      });

    // Successful writes leave the slice in 'success' but blank lastError so the
    // caller can refetch the list. We deliberately do NOT splice the payload
    // into items locally — Q6 conflict resolution + denorm party_role mean the
    // BE is the source of truth for the displayed row shape.
    builder
      .addCase(createUserPartyAssignment.fulfilled, (state) => {
        state.lastError = null;
      })
      .addCase(updateUserPartyAssignment.fulfilled, (state) => {
        state.lastError = null;
      })
      .addCase(deleteUserPartyAssignment.fulfilled, (state, action) => {
        state.lastError = null;
        state.items = state.items.filter((x) => x.id !== action.payload.assignmentId);
        state.totalCount = Math.max(0, state.totalCount - 1);
      });

    // updateUserAffiliation does not touch the assignments list — included in
    // this slice solely so the thunk can be co-located with party-assignment
    // domain logic. No state mutation needed.
    builder.addCase(updateUserAffiliation.rejected, (state, action) => {
      state.lastError = (action.payload as string) ?? action.error.message ?? 'Failed to update affiliation';
    });
  },
});

export const { clearError } = userPartyAssignmentSlice.actions;
export default userPartyAssignmentSlice.reducer;
